<template>
  <div class="modal fade" tabindex="-1" :id="id" ref="modal">
    <div class="modal-dialog modal-dialog-scrollable" :class="usesWideEditor ? 'modal-xl' : 'modal-lg'">
      <form method="post" class="modal-content" @submit.prevent="submit">
        <div class="modal-header">
          <div>
            <h5 class="modal-title mb-0">{{ isEditMode ? 'Editar atributo' : 'Adicionar atributo' }}</h5>
            <div class="text-muted fs-7 mt-1" v-if="subtitle">{{ subtitle }}</div>
          </div>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Fechar">
            <i class="bi bi-x-lg"></i>
          </div>
        </div>

        <div class="modal-body">
          <div class="mb-6">
            <div class="fs-7 text-uppercase text-muted fw-bold mb-3">Identificação</div>
            <div class="row g-4">
              <div class="col-md-7">
                <label :for="`${id}_label`" class="required form-label">Nome do atributo</label>
                <input class="form-control form-control-solid" :class="{'is-invalid': errors.label}"
                       v-model="form.label" :id="`${id}_label`" maxlength="255"
                       placeholder="Ex.: Queixa principal" required>
                <div class="invalid-feedback" v-if="errors.label">{{ errors.label[0] }}</div>
              </div>
              <div class="col-md-5">
                <label :for="`${id}_type`" class="required form-label">Tipo</label>
                <select class="form-select form-select-solid" :class="{'is-invalid': errors.type}"
                        :id="`${id}_type`" :value="form.type" @change="onTypeChange($event)" required>
                  <option value="" disabled v-if="!isEditMode">--Selecione--</option>
                  <option :value="attributeType.id" v-for="attributeType in attributeTypes" :key="attributeType.id">
                    {{ attributeType.description }}
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.type">{{ errors.type[0] }}</div>
              </div>
            </div>
          </div>

          <div class="separator separator-dashed mb-6"></div>

          <div class="mb-6">
            <div class="fs-7 text-uppercase text-muted fw-bold mb-3">Comportamento</div>
            <div class="row g-4">
              <div class="col-md-6" v-for="toggle in visibleToggles" :key="toggle.key">
                <label class="border rounded p-4 h-100 bg-light d-block mb-0 cursor-pointer"
                       :for="`${id}_${toggle.key}`">
                  <span class="d-flex justify-content-between align-items-start gap-3">
                    <span>
                      <span class="fw-semibold d-block">{{ toggle.title }}</span>
                      <span class="text-muted fs-8">{{ toggle.description }}</span>
                    </span>
                    <span class="form-check form-switch form-check-custom form-check-solid m-0">
                      <input class="form-check-input" type="checkbox" role="switch"
                             v-model="form[toggle.key]" :id="`${id}_${toggle.key}`">
                    </span>
                  </span>
                  <small class="text-danger d-block mt-2" v-if="errors[toggle.key]">{{ errors[toggle.key][0] }}</small>
                </label>
              </div>
            </div>
          </div>

          <template v-if="supportsDefaultText">
            <div class="separator separator-dashed mb-6"></div>

            <div class="mb-6">
              <div class="fs-7 text-uppercase text-muted fw-bold mb-3">Exibição e preenchimento</div>
              <div class="row g-4">
                <div class="col-md-4">
                  <label :for="`${id}_text_rows`" class="required form-label">Linhas mínimas na tela</label>
                  <input class="form-control form-control-solid" :class="{'is-invalid': errors.text_rows}"
                         type="number" min="1" max="20" v-model="form.text_rows" :id="`${id}_text_rows`" required>
                  <div class="invalid-feedback" v-if="errors.text_rows">{{ errors.text_rows[0] }}</div>
                </div>
                <div class="col-12">
                  <label :for="`${id}_default_text`" class="form-label">Texto padrão</label>
                  <textarea v-if="form.type === 'text'" class="form-control form-control-solid"
                            :class="{'is-invalid': errors.default_text}"
                            v-model="form.default_text" :id="`${id}_default_text`"
                            :rows="form.text_rows || 3"></textarea>
                  <QuillEditor v-else
                               :options="defaultTextEditorOptions"
                               class="attr-default-text-editor"
                               :id="`${id}_default_text`"
                               placeholder="Digite aqui..."
                               :value="form.default_text || ''"
                               @change="onDefaultTextChange"/>
                  <div class="form-text">{{ defaultTextHint }}</div>
                  <small class="text-danger d-block" v-if="errors.default_text">{{ errors.default_text[0] }}</small>
                </div>
              </div>
            </div>
          </template>

          <template v-if="usesOptionList">
            <div class="separator separator-dashed mb-6"></div>

            <div>
              <div class="fs-7 text-uppercase text-muted fw-bold mb-3">
                Opções <span class="text-gray-500">({{ optionList.length }})</span>
              </div>

              <div class="row g-3 align-items-end">
                <div class="col-md-4">
                  <label :for="`${id}_option`" class="form-label">Opção</label>
                  <input ref="optionInput" class="form-control form-control-solid" type="text" maxlength="200"
                         v-model="optionDraft.option" :id="`${id}_option`" placeholder="Ex.: Sim"
                         @keydown.enter.prevent="saveOptionDraft">
                </div>
                <div class="col-md-3">
                  <label :for="`${id}_option_require_compl`" class="form-label">Complemento</label>
                  <label class="form-control form-control-solid d-flex align-items-center gap-3 mb-0 cursor-pointer"
                         :for="`${id}_option_require_compl`">
                    <span class="form-check form-switch form-check-custom form-check-solid m-0">
                      <input class="form-check-input h-20px w-30px" type="checkbox" role="switch"
                             v-model="optionDraft.require_compl" :id="`${id}_option_require_compl`">
                    </span>
                    <span class="fs-7">{{ optionDraft.require_compl ? 'Sim' : 'Não' }}</span>
                  </label>
                </div>
                <div class="col-md-3">
                  <label :for="`${id}_option_label`" class="form-label">Título do complemento</label>
                  <input class="form-control form-control-solid" type="text" v-model="optionDraft.label"
                         :id="`${id}_option_label`" :disabled="!optionDraft.require_compl"
                         :placeholder="optionDraft.require_compl ? 'Ex.: Qual?' : 'Sem complemento'"
                         @keydown.enter.prevent="saveOptionDraft">
                </div>
                <div class="col-md-2 d-flex gap-2">
                  <button type="button" class="btn btn-primary flex-grow-1" :disabled="!optionDraft.option.trim()"
                          @click="saveOptionDraft">
                    {{ editingOptionIndex === null ? 'Incluir' : 'Alterar' }}
                  </button>
                  <button type="button" class="btn btn-icon btn-light" title="Cancelar edição"
                          v-if="editingOptionIndex !== null" @click="resetOptionDraft">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
              <small class="text-danger d-block mt-2" v-if="errors.options">{{ errors.options[0] }}</small>

              <div class="mt-5">
                <div class="text-muted fs-7 border border-dashed rounded p-4 text-center" v-if="!optionList.length">
                  Nenhuma opção cadastrada.
                </div>
                <draggable v-else v-model="form.options" tag="div" item-key="option" handle=".option-handle"
                           :disabled="editingOptionIndex !== null">
                  <template #item="{ element, index }">
                    <div class="d-flex align-items-center gap-3 border rounded px-3 py-2 mb-2"
                         :class="{'border-primary bg-light-primary': editingOptionIndex === index}">
                      <i class="bi bi-grip-vertical fs-4 text-muted option-handle"></i>
                      <span class="text-muted fw-semibold">{{ index + 1 }}</span>
                      <div class="flex-grow-1 text-break">
                        {{ element.option }}
                        <span class="badge badge-light-info ms-2" v-if="element.require_compl">
                          complemento: {{ element.label || 'sem título' }}
                        </span>
                      </div>
                      <button type="button" class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary"
                              title="Editar opção" @click="editOption(index)">
                        <i class="bi bi-pencil-square"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-icon btn-bg-light btn-active-color-danger"
                              title="Excluir opção" @click="deleteOption(index)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </template>
                </draggable>
              </div>
            </div>
          </template>

          <AttributeOptionsEditor
              v-if="usesOptionsEditor"
              v-model="form.options"
              :type="form.type"
              :grid-presets="gridPresets"
              :image-assets="imageAssets"
          />
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
          <button type="submit" class="btn btn-primary">{{ isEditMode ? 'Salvar' : 'Cadastrar' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import QuillEditor from "vue-quill-editor/src/editor";
import AttributeOptionsEditor from "./AttributeOptionsEditor.vue";

const IMPORT_LAST_RECORD_TYPES = ['text', 'html', 'number', 'checkbox', 'radio'];
const DEFAULT_TEXT_TYPES = ['text', 'html'];
const OPTION_LIST_TYPES = ['radio', 'checkbox'];
const OPTIONS_EDITOR_TYPES = ['grid', 'image_annotation', 'structured_list', 'odontogram'];
const WIDE_EDITOR_TYPES = ['grid', 'structured_list', 'odontogram'];

export default {
  name: "AttributeFormModal",
  components: {draggable, QuillEditor, AttributeOptionsEditor},
  emits: ['submit'],
  props: {
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      default: 'store',
      validator: (value) => ['store', 'edit'].includes(value),
    },
    form: {
      type: Object,
      required: true,
    },
    errors: {
      type: [Object, Array],
      default: () => ({}),
    },
    attributeTypes: {
      type: Array,
      default: () => [],
    },
    gridPresets: {
      type: Object,
      default: () => ({}),
    },
    imageAssets: {
      type: Array,
      default: () => [],
    },
    rectypeName: {
      type: String,
      default: '',
    },
    transcriptionEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      optionDraft: this.emptyOptionDraft(),
      editingOptionIndex: null,
      originalLabel: '',
      defaultTextEditorOptions: {
        modules: {toolbar: [['bold', 'italic'], [{'list': 'ordered'}, {'list': 'bullet'}]]},
        theme: 'snow',
      },
    };
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    subtitle() {
      return [this.rectypeName, this.isEditMode ? this.originalLabel : '']
        .filter(Boolean)
        .join(' · ');
    },
    supportsDefaultText() {
      return DEFAULT_TEXT_TYPES.includes(this.form.type);
    },
    supportsImportLastRecord() {
      return IMPORT_LAST_RECORD_TYPES.includes(this.form.type);
    },
    usesOptionList() {
      return OPTION_LIST_TYPES.includes(this.form.type);
    },
    usesOptionsEditor() {
      return OPTIONS_EDITOR_TYPES.includes(this.form.type);
    },
    usesWideEditor() {
      return WIDE_EDITOR_TYPES.includes(this.form.type);
    },
    optionList() {
      return Array.isArray(this.form.options) ? this.form.options : [];
    },
    visibleToggles() {
      return [
        {key: 'required', title: 'Obrigatório', description: 'Exige preenchimento ao salvar o prontuário', visible: true},
        {key: 'active', title: 'Ativo', description: 'Exibido nos novos prontuários', visible: true},
        {
          key: 'import_last_record',
          title: 'Importar último registro',
          description: 'Traz o valor do atendimento anterior',
          visible: this.supportsImportLastRecord,
        },
        {
          key: 'filled_by_ai',
          title: 'Preencher com IA',
          description: 'Usa a transcrição da consulta para preencher',
          visible: this.transcriptionEnabled,
        },
      ].filter((toggle) => toggle.visible);
    },
    defaultTextHint() {
      if (this.supportsImportLastRecord && this.form.import_last_record) {
        return 'Usado ao criar um novo prontuário quando o último registro não tiver valor para este atributo.';
      }
      return 'Preenchido automaticamente ao criar um novo prontuário.';
    },
  },
  mounted() {
    this.$refs.modal.addEventListener('show.bs.modal', this.onModalShow);
    this.$refs.modal.addEventListener('hidden.bs.modal', this.resetOptionDraft);
  },
  beforeUnmount() {
    this.$refs.modal.removeEventListener('show.bs.modal', this.onModalShow);
    this.$refs.modal.removeEventListener('hidden.bs.modal', this.resetOptionDraft);
  },
  methods: {
    emptyOptionDraft() {
      return {option: '', require_compl: false, label: ''};
    },
    onModalShow() {
      this.originalLabel = this.form.label ?? '';
      this.resetOptionDraft();
    },
    hasConfiguredOptions() {
      if (Array.isArray(this.form.options)) {
        return this.form.options.length > 0;
      }
      return !!this.form.options && typeof this.form.options === 'object';
    },
    async onTypeChange(event) {
      const newType = event.target.value;
      const previousType = this.form.type;
      const bothAreOptionLists = OPTION_LIST_TYPES.includes(previousType) && OPTION_LIST_TYPES.includes(newType);
      const previousUsesOptions = OPTION_LIST_TYPES.includes(previousType) || OPTIONS_EDITOR_TYPES.includes(previousType);

      if (this.isEditMode && previousUsesOptions && !bothAreOptionLists && this.hasConfiguredOptions()) {
        const result = await Swal.fire({
          title: 'Trocar o tipo?',
          text: 'As opções configuradas para o tipo atual serão perdidas.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, trocar',
          cancelButtonText: 'Cancelar',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-secondary',
          },
        });

        if (!result.isConfirmed) {
          event.target.value = previousType;
          return;
        }
      }

      if (OPTION_LIST_TYPES.includes(newType) && !bothAreOptionLists) {
        this.form.options = [];
      }
      this.form.type = newType;
      this.resetOptionDraft();
    },
    onDefaultTextChange(event) {
      if (typeof event?.html !== 'string') {
        return;
      }
      this.form.default_text = event.html;
    },
    resetOptionDraft() {
      this.optionDraft = this.emptyOptionDraft();
      this.editingOptionIndex = null;
    },
    saveOptionDraft() {
      if (!this.optionDraft.option.trim()) {
        return;
      }

      const option = {
        option: this.optionDraft.option.trim(),
        require_compl: this.optionDraft.require_compl,
        label: this.optionDraft.require_compl ? this.optionDraft.label : '',
      };

      if (!Array.isArray(this.form.options)) {
        this.form.options = [];
      }

      if (this.editingOptionIndex === null) {
        this.form.options.push(option);
      } else {
        this.form.options[this.editingOptionIndex] = option;
      }

      this.resetOptionDraft();
      this.$nextTick(() => this.$refs.optionInput?.focus());
    },
    editOption(index) {
      this.optionDraft = {...this.emptyOptionDraft(), ...this.form.options[index]};
      this.editingOptionIndex = index;
      this.$nextTick(() => this.$refs.optionInput?.focus());
    },
    deleteOption(index) {
      Swal.fire({
        title: 'Heeyy',
        text: 'Tem certeza que deseja remover a opção?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.form.options.splice(index, 1);
          this.resetOptionDraft();
        }
      });
    },
    submit() {
      this.$emit('submit', {
        ...this.form,
        default_text: this.supportsDefaultText ? (this.form.default_text || null) : null,
      });
    },
  },
};
</script>

<style scoped>
.option-handle {
  cursor: move;
}

.attr-default-text-editor :deep(.ql-editor) {
  min-height: 120px;
}
</style>
